/*
 * Wishlist store, for every theme (the theme draws it, this keeps it).
 *
 * Item: {type: 'variant' | 'bundle', id, title, price, image, url}.
 * - Guest: localStorage only.
 * - Logged in (login_token cookie): Prodyflow via /api/wishlist*, shared with
 *   the app; localStorage mirrors it so the first paint needs no request.
 *   Items saved as a guest are merged into the account on the first load
 *   after login. The list is synced on a page load only when the last sync
 *   is 10 minutes old or more (SYNC_MS; every answer of the API counts as a
 *   sync, so does a change), else the local copy is used as it is. A sync
 *   also refreshes every item's title, price, image and link from the shop's
 *   current data: a logged-in customer asks the list itself with `refresh`
 *   (one request: the engine gives the fresh cards and saves the changes to
 *   the account), a guest asks /api/wishlist/cards for the cards of the
 *   local list.
 *
 * window.WishlistStore: all(), has(type, id), toggle(item), remove(type, id),
 * onChange(callback). Changes also fire a `wishlist:change` event on document.
 */
const WishlistStore = {
    KEY: 'wishlist',
    GUEST_KEY: 'wishlist_guest',
    SYNC_KEY: 'wishlist_synced',
    SYNC_MS: 10 * 60 * 1000,
    items: [],
    listeners: [],
    loaded: false,

    token() {
        return (window.Cookies && Cookies.getItem('login_token')) || '';
    },
    // who the local copy belongs to (a short hash of the token, not the token): another login is never "fresh"
    owner() {
        const token = this.token();
        let hash = 0;
        for (let i = 0; i < token.length; i++) hash = (hash * 31 + token.charCodeAt(i)) | 0;
        return String(hash >>> 0);
    },
    markSynced() {
        try { localStorage.setItem(this.SYNC_KEY, Date.now() + ':' + this.owner()); } catch (e) { /* ignore */ }
    },
    // the local copy came from the server less than SYNC_MS ago, for this login
    fresh() {
        try {
            const [at, owner] = (localStorage.getItem(this.SYNC_KEY) || '').split(':');
            const age = Date.now() - Number(at);
            return owner === this.owner() && age >= 0 && age < this.SYNC_MS;
        } catch (e) {
            return false;
        }
    },
    key(type, id) {
        return (type || 'variant') + ':' + id;
    },
    read(key) {
        try {
            const list = JSON.parse(localStorage.getItem(key) || '[]');
            return Array.isArray(list) ? list.map((item) => Object.assign({ type: 'variant' }, item)) : [];
        } catch (e) {
            return [];
        }
    },
    write(key, list) {
        try {
            localStorage.setItem(key, JSON.stringify(list));
        } catch (e) {
            // private window / full storage: the list still works for this page
        }
    },

    // starts on first use (a theme with hearts calls it): pages that have no wishlist do not touch
    // the browser storage or the API at all
    ensure() {
        if (!this.loaded) this.load();
    },
    all() {
        this.ensure();
        return this.items.slice();
    },
    has(type, id) {
        this.ensure();
        const k = this.key(type, id);
        return this.items.some((item) => this.key(item.type, item.id) === k);
    },
    onChange(callback) {
        this.ensure();
        this.listeners.push(callback);
    },
    changed() {
        this.write(this.KEY, this.items);
        if (!this.token()) {
            this.write(this.GUEST_KEY, this.items);
        }
        this.listeners.forEach((callback) => {
            try { callback(this.all()); } catch (e) { /* a listener must not break the others */ }
        });
        document.dispatchEvent(new CustomEvent('wishlist:change', { detail: this.all() }));
    },

    toggle(item) {
        this.ensure();
        const type = item.type || 'variant';
        if (this.has(type, item.id)) {
            this.remove(type, item.id);
            return false;
        }
        const entry = { type, id: item.id, title: item.title || '', price: item.price || '', image: item.image || '', url: item.url || '' };
        this.items.unshift(entry);
        this.changed();
        if (this.token()) {
            this.api('/api/wishlist/add', { items: [this.toApi(entry)] });
        }
        return true;
    },
    remove(type, id) {
        this.ensure();
        const k = this.key(type, id);
        this.items = this.items.filter((item) => this.key(item.type, item.id) !== k);
        this.changed();
        if (this.token()) {
            this.api('/api/wishlist/remove', { type: type || 'variant', id });
        }
    },

    toApi(item) {
        return { type: item.type, id: item.id, data: { title: item.title, price: item.price, image: item.image, url: item.url } };
    },
    async api(url, body) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: window.http ? http.headers() : { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify(Object.assign({ token: this.token() }, body || {})),
            });
            if (response.status === 401) {
                return null; // logged out meanwhile: stay with the local list
            }
            const json = await response.json();
            if (json && json.success && Array.isArray(json.data)) {
                this.items = json.data.map((item) => ({
                    type: item.type, id: String(item.id), title: item.title || '', price: item.price || '', image: item.image || '', url: item.url || '',
                }));
                this.markSynced();
                this.changed();
            }
            return json;
        } catch (e) {
            return null;
        }
    },

    // a guest's items: title, price, image and link from the shop's current data (the account's list is refreshed by the
    // engine, in the list request itself)
    async refresh() {
        if (!this.items.length) {
            this.markSynced();
            return;
        }
        try {
            const response = await fetch('/api/wishlist/cards', {
                method: 'POST',
                headers: window.http ? http.headers() : { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: this.items.map((item) => ({ type: item.type, id: item.id })) }),
            });
            const json = response.ok ? await response.json() : null;
            if (!json || !json.success || !Array.isArray(json.data)) {
                return;
            }
            const fresh = {};
            json.data.forEach((card) => { fresh[this.key(card.type, card.id)] = card; });
            let changed = false;
            this.items.forEach((item) => {
                const card = fresh[this.key(item.type, item.id)];
                if (!card) return; // gone from the shop: the saved card stays
                // the card's url is a path inside the sale channel: the page's prefix goes in front
                if (card.url) card.url = (window.SHOP_BASE || '') + card.url;
                ['title', 'price', 'image', 'url'].forEach((field) => {
                    if (card[field] && card[field] !== item[field]) {
                        item[field] = card[field];
                        changed = true;
                    }
                });
            });
            this.markSynced();
            if (changed) this.changed();
        } catch (e) {
            // offline or an error: the saved cards stay, the next load tries again
        }
    },

    async load() {
        if (this.loaded) return;
        this.loaded = true;
        this.items = this.read(this.KEY).map((item) => Object.assign(item, { id: String(item.id) }));

        this.changed();
        const token = this.token();
        // the copy is recent: no request (a guest list waiting for the account is never "recent")
        const guest = token ? this.read(this.GUEST_KEY) : [];
        const stale = guest.length > 0 || !this.fresh();
        if (!stale) {
            return;
        }
        if (!token) {
            await this.refresh();
            return;
        }
        // the account's list comes back refreshed (title, price, image, link), in this one request
        const refresh = { refresh: true, base: window.SHOP_BASE || '' };
        if (guest.length) {
            // first load after login: what was saved as a guest goes to the account
            await this.api('/api/wishlist/add', Object.assign({ items: guest.map((item) => this.toApi(item)) }, refresh));
            try { localStorage.removeItem(this.GUEST_KEY); } catch (e) { /* ignore */ }
        } else {
            await this.api('/api/wishlist', refresh);
        }
    },
};

window.WishlistStore = WishlistStore;
